import { z } from 'my-zod'
import { manualCreditAdjustmentsModel } from '../select-membership/credit'
import { customerCardModel, customersModel } from '../customer-operations/customer'
import { customerOrdersModel } from '../customer-operations/customer-order'
import { customerOrderLineChargesModel } from '../customer-operations/customer-order-line-charge'
import { customerOrderPaymentsModel } from '../customer-operations/customer-order-payment'
import { customerSubscriptionModel } from '../select-membership/customer-subscription'
import { customerWaiversModel } from '../customer-operations/customer-waiver'
import { ReservationSchemas } from '../customer-operations/reservation'
import { subscriptionPlanModel } from '../select-membership/subscription-plan'
import { subscriptionRenewalsModel } from '../select-membership/subscription-renewal'

import { billingReportLineModel, performanceByMonthModel } from '../finance/finance-report'
import { outfitterInvoicesModel } from '../finance/outfitter-invoice'
import { transferModel } from '../finance/transfers'
import { customAssetClassModel } from '../fleet-operations/custom-asset-class'
import { customAssetFamilyModel } from '../fleet-operations/custom-asset-family'
import { dealersModel } from '../fleet-operations/dealer'
import { modelsModel } from '../fleet-operations/model'
import { Model2Schemas } from '../fleet-operations/model2'
import { modelFamiliesModel } from '../fleet-operations/model-family'
import { modelForecastsModel } from '../fleet-operations/model-forecast'
import { modelGroupsModel } from '../fleet-operations/model-group'
import { serviceBulletinsModel } from '../fleet-operations/service-bulletin'
import {
  vehicleAdventureView,
  customAssetView,
  vehicleCorporateFleetView,
  vehicleEmployeePolarisOwnedUnitsView,
  vehicleSystemView
} from '../fleet-operations/vehicle'
import { outfitterOrderVehicleAllocationsModel } from '../fleet-operations/vehicle-allocation'
import { vehicleDispositionsModel } from '../fleet-operations/vehicle-disposition'
import { vehicleLocationModel } from '../fleet-operations/vehicle-location'
import { vehicleMetricModel } from '../fleet-operations/vehicle-metric'
import { warehousesModel } from '../fleet-operations/warehouse'
import { workOrderModel } from '../fleet-operations/work-order'
import { maintenanceProgramModel } from '../fleet-operations/maintenance-programs'
import { serviceTaskModel } from '../fleet-operations/service-tasks'
import { serviceReminderAdjustmentModel } from '../fleet-operations/service-reminder-adjustment'
import { accountCaseModel } from '../outfitter-account-management/account-cases'
import { billingCaseModel } from '../outfitter-account-management/billing-cases'
import { complianceCaseModel } from '../outfitter-account-management/compliance-cases'
import {
  damageWaiverCaseModel,
  involvedPartyModel,
  partsOrderModel
} from '../outfitter-account-management/damage-waiver-cases'
import { operatorsModel } from '../outfitter-account-management/outfitter'
import { outfitterBlackoutDateModel } from '../outfitter-account-management/outfitter-blackout-date'
import { outfitterCommunicationsLogModel } from '../outfitter-account-management/outfitter-communications-log'
import { outfitterContactsModel } from '../outfitter-account-management/outfitter-contacts'
import { outfitterDocumentModel } from '../outfitter-account-management/outfitter-documents'
import { operatorGroupsModel } from '../outfitter-account-management/outfitter-group'
import { outfitterForecastModel } from '../outfitter-account-management/outfitter-forecast'
import {
  outfitterOrderHeadersModel,
  outfitterOrderLinesModel,
  outfitterOrderWarehouseAllocationsModel
} from '../outfitter-account-management/outfitter-order'
import { salesQuoteHeadersModel } from '../outfitter-account-management/sales-quote'
import { VehicleOrder2Schemas } from '../outfitter-account-management/vehicle-order-2'
import { outfitterInventoryModel } from '../outfitter-account-management/outfitter-inventory'
import { programAgreementModel } from '../outfitter-account-management/program-agreement'

import { damageWaiverAgreementsModel } from '../outfitter-operations/damage-waiver-agreement'
import { damageWaiverPlansModel } from '../outfitter-operations/damage-waiver-plan'
import { devicesModel } from '../outfitter-operations/device'
import { gearListModel } from '../outfitter-operations/gear'
import { instructionalContentModel } from '../outfitter-operations/instructions'
import { vehicleUseHistoryModel } from '../outfitter-operations/vehicle-use-history'
import { polarisWaiverContentTemplateModel } from '../outfitter-operations/polaris-waiver-content-template'
import { outfitterWaiverContentModel } from '../outfitter-operations/outfitter-waiver-content'
import {
  actionPlanTemplateModel,
  actionPlanTemplateTaskGroupModel,
  actionPlanTemplateTaskModel
} from '../utilities/action-plan-templates'
import { actionPlanModel, actionPlanTaskGroupModel, actionPlanTaskModel } from '../utilities/action-plans'
import { addOnModel } from '../customer-operations/add-on/add-on-model'
import { announcementModel } from '../utilities/announcement'
import { changeLogEntryModel } from '../utilities/change-log'
import { commentModel } from '../utilities/comment'
import { outfitterMobileReleasesModel } from '../utilities/outfitter-app-mobile-release'
import { outfitterUsersModel } from '../utilities/outfitter-user'
import { revisionModel } from '../utilities/revision'
import { tableViewsModel } from '../utilities/table-view'
import { polarisUsersModel } from '../utilities/user'
import { wmsMobileReleasesModel } from '../utilities/wms-app-mobile-release'
import { promotionModel } from '../customer-operations/promotion'
import { merchandiseAddOnProduct, rentalEquipmentAddOnProduct } from '../customer-operations/add-on-product'
import { askPolarisCaseModel } from '../fleet-operations/ask-polaris-cases'
import { outfitterGroupAssignmentsModel } from '../fleet-operations/outfitter-group-assignments'
import { polarisRentalProductUpdate } from '../customer-operations/polaris-rental-product/polaris-rental-product-update'
import { RentalProductMetricsSchemas } from '../customer-operations/rental-product-metrics'
import { customerPostRideSurveyResultsV2Model } from '../customer-operations/post-ride-survey'
import { customerOrderDisputesModel } from '../finance/customer-order-disputes'
import { affiliatesModel } from '../customer-operations/affiliates'
import { taxPolicyModel } from '../finance/tax-policies'
import { RideInsuranceConfigSchemas } from '../finance/ride-insurance-config'
import { FeeAgreementSchemas } from '../finance/fee-agreement'
import { outfitterMessageTemplateModel } from '../outfitter-operations/outfitter-message-template'
import { viatorRentalProductUpdate } from '../customer-operations/viator/viator-rental-product'
import { GiftCardSchemas } from '../customer-operations/gift-card'
import { terminalModel } from '../outfitter-operations/terminal'
import { outfitterFinancialConfigurationModel } from '../finance/outfitter-financial-configuration'

// if you want to add a subject, but there is no model for the subject, use this as the schema
const NO_SCHEMA = z.never()

/**
 * The key is the name you are giving the subject. The value is the model schema
 * for this subject
 */
export const Schema = {
  appVersion: NO_SCHEMA,
  accountCases: accountCaseModel,
  actionPlan: actionPlanModel,
  actionPlanTask: actionPlanTaskModel,
  actionPlanTaskGroup: actionPlanTaskGroupModel,
  actionPlanTemplate: actionPlanTemplateModel,
  actionPlanTemplateTask: actionPlanTemplateTaskModel,
  actionPlanTemplateTaskGroup: actionPlanTemplateTaskGroupModel,
  actionPlanTrigger: NO_SCHEMA,
  adventuresVehicles: vehicleAdventureView,
  announcements: announcementModel,
  billingCases: billingCaseModel,
  billingReportLine: billingReportLineModel,
  changeLogEntry: changeLogEntryModel,
  comment: commentModel,
  complianceCases: complianceCaseModel,
  customAssetClasses: customAssetClassModel,
  customAssetFamilies: customAssetFamilyModel,
  customAssets: customAssetView,
  customerCard: customerCardModel,
  customerOrderLineCharges: customerOrderLineChargesModel,
  customerOrderPayments: customerOrderPaymentsModel,
  customerOrderDisputes: customerOrderDisputesModel,
  reservations: ReservationSchemas.reservationModel,
  addons: addOnModel,
  customerOrders: customerOrdersModel,
  customers: customersModel,
  customerSubscription: customerSubscriptionModel,
  customerWaivers: customerWaiversModel,
  askPolarisCases: askPolarisCaseModel,
  damageWaiverAgreements: damageWaiverAgreementsModel,
  damageWaiverCases: damageWaiverCaseModel,
  damageWaiverPlans: damageWaiverPlansModel,
  dealers: dealersModel,
  devCommands: NO_SCHEMA,
  devices: devicesModel,
  feeAgreements: FeeAgreementSchemas.feeAgreementModel,
  gearList: gearListModel,
  giftCard: GiftCardSchemas.giftCardModel,
  instructionalContent: instructionalContentModel,
  involvedParties: involvedPartyModel,
  manualCreditAdjustmentsModel: manualCreditAdjustmentsModel,
  modelFamilies: modelFamiliesModel,
  modelForecasts: modelForecastsModel,
  modelGroups: modelGroupsModel,
  models: modelsModel,
  models2: Model2Schemas.model2Model,
  operatorGroups: operatorGroupsModel,
  operators: operatorsModel,
  outfitterForecasts: outfitterForecastModel,
  connectedBillingAccount: NO_SCHEMA,
  outfitterBlackoutDates: outfitterBlackoutDateModel,
  outfitterCommunicationsLog: outfitterCommunicationsLogModel,
  outfitterContacts: outfitterContactsModel,
  outfitterDocuments: outfitterDocumentModel,
  outfitterFinancialConfigurations: outfitterFinancialConfigurationModel,
  outfitterGroupAssignments: outfitterGroupAssignmentsModel,
  outfitterInventory: outfitterInventoryModel,
  outfitterInvoices: outfitterInvoicesModel,
  outfitterMobileReleases: outfitterMobileReleasesModel,
  outfitterOrderHeaders: outfitterOrderHeadersModel,
  outfitterOrderLines: outfitterOrderLinesModel,
  outfitterOrderVehicleAllocations: outfitterOrderVehicleAllocationsModel,
  outfitterOrderWarehouseAllocations: outfitterOrderWarehouseAllocationsModel,
  outfitterUsers: outfitterUsersModel,
  partOrders: partsOrderModel,
  performanceByMonth: performanceByMonthModel,
  polarisUsers: polarisUsersModel,
  rentalProductMetrics: RentalProductMetricsSchemas.rentalProductMetricsModel,
  rideInsuranceConfig: RideInsuranceConfigSchemas.rideInsuranceConfig,
  programAgreements: programAgreementModel,
  promotions: promotionModel,
  revision: revisionModel,
  rentalEquipmentAddOnProducts: rentalEquipmentAddOnProduct,
  merchandiseAddOnProducts: merchandiseAddOnProduct,
  salesQuoteHeaders: salesQuoteHeadersModel,
  serviceBulletins: serviceBulletinsModel,
  subscriptionPlan: subscriptionPlanModel,
  subscriptionRenewalsModel: subscriptionRenewalsModel,
  transferModel: transferModel,
  tableViews: tableViewsModel,
  vehicleDispositions: vehicleDispositionsModel,
  vehicleLocations: vehicleLocationModel,
  vehicleMetrics: vehicleMetricModel,
  polarisRentalProducts: polarisRentalProductUpdate,
  viatorRentalProducts: viatorRentalProductUpdate,
  vehicles: vehicleSystemView,
  vehicleOrders: VehicleOrder2Schemas.vehicleOrderHeadersModel,
  vehicleOrderDiscountConfigurations: VehicleOrder2Schemas.vehicleOrderDiscountConfiguration,
  corporateFleet: vehicleCorporateFleetView,
  employeePolarisOwnedUnits: vehicleEmployeePolarisOwnedUnitsView,
  vehicleUseHistory: vehicleUseHistoryModel,
  waiverContentTemplates: polarisWaiverContentTemplateModel,
  outfitterWaiverContent: outfitterWaiverContentModel,
  outfitterMessageTemplates: outfitterMessageTemplateModel,
  warehouses: warehousesModel,
  wmsMobileReleases: wmsMobileReleasesModel,
  workOrders: workOrderModel,
  testSms: NO_SCHEMA,
  waiverLinkSms: NO_SCHEMA,
  reviewRatingLinkSms: NO_SCHEMA,
  smsMessageToRider: NO_SCHEMA,
  serviceTasks: serviceTaskModel,
  maintenancePrograms: maintenanceProgramModel,
  serviceReminderAdjustments: serviceReminderAdjustmentModel,
  customerPostRideSurvey: customerPostRideSurveyResultsV2Model,
  affiliates: affiliatesModel,
  taxPolicy: taxPolicyModel,
  ordway: NO_SCHEMA,
  terminal: terminalModel,
  outfitterFinancialDetailReports: NO_SCHEMA,
  outfitterGeneralReports: NO_SCHEMA
} satisfies { [key: string]: z.ZodTypeAny }
